import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function HowItWorks() {
  const [activeTab, setActiveTab] = useState<'renter' | 'host'>('renter');

  const renterSteps = [
    {
      number: '01',
      title: 'Search & Discover',
      description: 'Browse thousands of spaces and assets across Australia. Use filters to find exactly what you need, when you need it.',
      icon: 'ri-search-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20australian%20person%20using%20smartphone%20searching%20storage%20marketplace%20bright%20clean%20minimalist%20background%20professional%20lifestyle%20photography%20natural%20daylight%20contemporary%20digital%20technology&width=600&height=400&seq=hiw01&orientation=landscape'
    },
    {
      number: '02',
      title: 'Compare & Choose',
      description: 'Review detailed listings with photos, pricing, availability, and verified reviews from other renters.',
      icon: 'ri-file-list-3-line',
      image: 'https://readdy.ai/api/search-image?query=clean%20organized%20australian%20suburban%20garage%20driveway%20storage%20space%20bright%20natural%20daylight%20professional%20real%20estate%20photography%20modern%20home%20exterior%20welcoming%20atmosphere&width=600&height=400&seq=hiw02&orientation=landscape'
    },
    {
      number: '03',
      title: 'Book Instantly',
      description: 'Select your dates, send a booking request, and communicate directly with the host. Most bookings are confirmed within hours.',
      icon: 'ri-calendar-check-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20australian%20person%20confirming%20online%20booking%20laptop%20calendar%20visible%20modern%20home%20office%20natural%20light%20organized%20workspace%20confident%20satisfied%20expression&width=600&height=400&seq=hiw03&orientation=landscape'
    },
    {
      number: '04',
      title: 'Access & Enjoy',
      description: 'Meet your host, access your space or asset, and enjoy hassle-free usage. All bookings are protected by our insurance policy.',
      icon: 'ri-key-2-line',
      image: 'https://readdy.ai/api/search-image?query=friendly%20australian%20host%20handing%20keys%20renter%20outside%20modern%20suburban%20home%20warm%20welcoming%20bright%20daylight%20professional%20lifestyle%20photography%20genuine%20smiles%20handshake&width=600&height=400&seq=hiw04&orientation=landscape'
    }
  ];

  const hostSteps = [
    {
      number: '01',
      title: 'List Your Space',
      description: 'Create a free listing in minutes. Add photos, set your price, and describe what makes your space special.',
      icon: 'ri-add-circle-line',
      image: 'https://readdy.ai/api/search-image?query=australian%20homeowner%20taking%20photos%20clean%20garage%20space%20smartphone%20bright%20natural%20lighting%20organized%20spacious%20interior%20professional%20documentation%20modern%20suburban&width=600&height=400&seq=hiw05&orientation=landscape'
    },
    {
      number: '02',
      title: 'Set Your Terms',
      description: 'Choose your availability, pricing, and house rules. You have complete control over who books and when.',
      icon: 'ri-settings-3-line',
      image: 'https://readdy.ai/api/search-image?query=professional%20australian%20person%20setting%20listing%20details%20laptop%20modern%20home%20office%20calendar%20pricing%20notes%20organized%20workspace%20confident%20business%20owner&width=600&height=400&seq=hiw06&orientation=landscape'
    },
    {
      number: '03',
      title: 'Accept Bookings',
      description: 'Review booking requests, communicate with renters, and approve bookings that work for you. Get instant notifications.',
      icon: 'ri-checkbox-circle-line',
      image: 'https://readdy.ai/api/search-image?query=australian%20host%20reviewing%20booking%20requests%20tablet%20notification%20alerts%20comfortable%20home%20setting%20natural%20lighting%20professional%20organized%20modern%20technology&width=600&height=400&seq=hiw07&orientation=landscape'
    },
    {
      number: '04',
      title: 'Earn Money',
      description: 'Get paid securely through our platform. Payments are released 24 hours after the booking starts. Track your earnings in real-time.',
      icon: 'ri-money-dollar-circle-line',
      image: 'https://readdy.ai/api/search-image?query=happy%20australian%20host%20checking%20earnings%20dashboard%20laptop%20positive%20income%20growth%20modern%20home%20office%20bright%20successful%20small%20business%20owner%20financial%20satisfaction&width=600&height=400&seq=hiw08&orientation=landscape'
    }
  ];

  const steps = activeTab === 'renter' ? renterSteps : hostSteps;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">How stashly Works</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Whether you're looking to rent a space or earn money from your unused assets, 
            we've made it simple, safe, and secure.
          </p>
        </div>
      </section>

      {/* Tab Switcher */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="bg-white rounded-full shadow-lg p-1 inline-flex mx-auto" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button
              onClick={() => setActiveTab('renter')}
              className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap ${
                activeTab === 'renter'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              I Want to Rent
            </button>
            <button
              onClick={() => setActiveTab('host')}
              className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap ${
                activeTab === 'host'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              I Want to Host
            </button>
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } gap-12 items-center`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full">
                      <i className={`${step.icon} text-3xl text-blue-600`}></i>
                    </div>
                    <span className="text-6xl font-bold text-gray-200">{step.number}</span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{step.description}</p>
                </div>
                <div className="flex-1">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-xl"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Safety is Our Priority
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every booking on stashly is protected by our comprehensive safety measures
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Verified Users</h3>
              <p className="text-gray-600">
                All users verify their identity and contact information before booking or hosting
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-indigo-100 rounded-full mx-auto mb-6">
                <i className="ri-lock-line text-3xl text-indigo-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Secure Payments</h3>
              <p className="text-gray-600">
                All transactions are processed securely. Your payment information is never shared
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-6">
                <i className="ri-message-3-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Instant Messaging</h3>
              <p className="text-gray-600">
                Direct communication between renters and hosts for quick questions and updates
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-6">
                <i className="ri-customer-service-2-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Support</h3>
              <p className="text-gray-600">
                Our dedicated support team is available around the clock to help with any issues
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Join thousands of Australians already using stashly
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/browse"
              className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Find a Space
            </a>
            <a
              href="/become-host"
              className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              List Your Space
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
